import { useEffect, useState } from 'react';
import type { ApiClient } from '../api';
import type { StVorgabe } from './elektro/sicherheitstechnik';
import { PDF_HINWEIS, zerlegeBefund } from './elektro/sicherheitstechnik';
import type {
  ZutrittBauteil, ZutrittMatrixzeile, ZutrittStand, ZutrittTuer, ZutrittVersorgung,
} from './elektro/zutritt';
import { statusname, versorgungsname, ZUTRITT_ANNAHMEN_HINWEIS, ZUTRITT_HINWEIS } from './elektro/zutritt';
import {
  ContentSection, DataTable, EmptyState, ErrorState, KpiCard, KpiReihe, LoadingState, StatusBadge,
  type Spalte, type Zustand,
} from '../muster';

/**
 * Reiter „Zutritt, Fluchttür, Türüberwachung" der Seite „Brandfallsteuerung und Notstrom" (W2-D3,
 * 17.10.2026).
 *
 * LIEST `GET …/elektro/sicherheitstechnik/zutritt`: je Tür die Anforderung aus Sicherheitskonzept
 * und Türbuch gegen Leser und Fluchttürterminals im Modell, die Zuordnung Bauteil → Tür und die
 * Versorgung. Geladen wird erst, wenn der Reiter offen ist. Ein PDF-Knopf (ELT-ZKF, neue
 * Berichtsfassung nur bei geändertem Stand, F11) und die Excel-Mappe.
 */
export function ZutrittAnsicht({ api, projektId }: { api: ApiClient; projektId: string }) {
  const [stand, setStand] = useState<ZutrittStand | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState<string | null>(null);

  useEffect(() => {
    let aktiv = true;
    api.elektroZutritt(projektId)
      .then((s) => { if (aktiv) { setStand(s ?? null); setFehler(null); } })
      .catch((e: unknown) => { if (aktiv) setFehler(e instanceof Error ? e.message : String(e)); });
    return () => { aktiv = false; };
  }, [api, projektId]);

  async function laden(art: 'nachweis' | 'excel') {
    setLaeuft(art);
    try {
      if (art === 'nachweis') await api.elektroZutrittNachweisLaden(projektId);
      else await api.elektroZutrittListen(projektId);
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(null);
    }
  }

  return (
    <>
      <div className="aktionen">
        <button type="button" className="m-knopf" disabled={laeuft !== null}
                onClick={() => { void laden('excel'); }}>
          Türabgleich-Excel-Mappe
        </button>
        <button type="button" className="m-knopf" disabled={laeuft !== null} title={PDF_HINWEIS}
                onClick={() => { void laden('nachweis'); }}>
          Türabgleich als PDF
        </button>
      </div>
      {fehler && <ErrorState titel="Der Türabgleich konnte nicht geladen werden." technisch={fehler} />}
      {!fehler && !stand && <LoadingState />}
      {stand && stand.kennzahlen.tueren === 0 && (
        <EmptyState titel="Kein Türbuch"
                    erklaerung="Türen entstehen über die Türaufnahme; ohne Türen gibt es nichts abzugleichen." />
      )}
      {stand && stand.kennzahlen.tueren > 0 && <Inhalt stand={stand} />}
    </>
  );
}

function zustandVon(status: string): Zustand {
  return status === 'erfuellt' ? 'gut'
    : status === 'fehlt' ? 'warnung'
      : status === 'nicht_geplant' ? 'fehler'
        : 'info';
}

function Statusmarke({ status }: { status: string }) {
  if (status === 'entfaellt') return <span className="klein">—</span>;
  return <StatusBadge zustand={zustandVon(status)} text={statusname(status)} klein />;
}

function Befundliste({ befunde }: { befunde: string[] }) {
  if (befunde.length === 0) return null;
  return (
    <ul className="liste">
      {befunde.map((b, i) => {
        const { stufe, text } = zerlegeBefund(b);
        const z: Zustand = stufe === 'fehler' ? 'fehler' : stufe === 'warnung' ? 'warnung' : 'info';
        return <li key={`${i}-${text.slice(0, 24)}`}><StatusBadge zustand={z} text={text} /></li>;
      })}
    </ul>
  );
}

function hatWarnung(t: ZutrittTuer): boolean {
  return t.befunde.some((b) => zerlegeBefund(b).stufe !== 'hinweis');
}

function Inhalt({ stand }: { stand: ZutrittStand }) {
  const k = stand.kennzahlen;
  return (
    <>
      <p className="hinweis">{ZUTRITT_HINWEIS}</p>
      {k.aufAnnahmen && <p className="hinweis hinweis-warnung">{ZUTRITT_ANNAHMEN_HINWEIS}</p>}
      <KpiReihe>
        <KpiCard titel="Türen im Abgleich" wert={k.tuerenImAbgleich} vergleich={`von ${k.tueren} Türen`} />
        <KpiCard titel="Leser" wert={k.leser} vergleich={`Fluchttürterminals ${k.terminals}`} />
        <KpiCard titel="Ohne Tür" wert={k.ohneTuer} zustand={k.ohneTuer > 0 ? 'warnung' : 'gut'}
                 zustandText={k.ohneTuer > 0 ? 'Zuordnung prüfen' : 'alle zugeordnet'} />
        <KpiCard titel="Ohne Versorgung" wert={k.ohneVersorgung + k.verteilerOhneKreis}
                 zustand={k.ohneVersorgung + k.verteilerOhneKreis > 0 ? 'warnung' : 'gut'}
                 zustandText={`ohne ${k.ohneVersorgung} · ohne Stromkreis ${k.verteilerOhneKreis}`} />
        <KpiCard titel="SV gefordert" wert={k.svGefordert} zustand={k.svAusNormalnetz > 0 ? 'warnung' : 'gut'}
                 zustandText={`aus Normalnetz ${k.svAusNormalnetz}`} />
        <KpiCard titel="Funk-Türblatt" wert={k.zutrittFunk} zustand={k.zutrittFunk > 0 ? 'warnung' : 'gut'}
                 zustandText={k.zutrittFunk > 0 ? 'ELT-97' : 'keins'} />
      </KpiReihe>
      <Matrix zeilen={stand.matrix} />
      <Tueren zeilen={stand.tueren} />
      <OhneTuer zeilen={stand.bauteile.filter((b) => b.tuer === null)} />
      <Versorgung zeilen={stand.versorgung} />
      {stand.befunde.length > 0 && (
        <ContentSection titel="Befunde Türabgleich">
          <Befundliste befunde={stand.befunde} />
        </ContentSection>
      )}
      <Vorgaben zeilen={stand.vorgaben} />
    </>
  );
}

function Matrix({ zeilen }: { zeilen: ZutrittMatrixzeile[] }) {
  const spalten: Spalte<ZutrittMatrixzeile>[] = [
    { schluessel: 'titel', titel: 'Gewerk', wert: (z) => z.titel },
    { schluessel: 'modell', titel: 'Bauteile im Modell', art: 'zahl', wert: (z) => z.bauteileImModell },
    { schluessel: 'konzept', titel: 'gefordert Konzept', art: 'zahl', wert: (z) => z.gefordertKonzept },
    { schluessel: 'buch', titel: 'gefordert Türbuch', art: 'zahl', wert: (z) => z.gefordertTuerbuch },
    { schluessel: 'gefordert', titel: 'gefordert', art: 'zahl', wert: (z) => z.gefordert },
    { schluessel: 'erfuellt', titel: 'erfüllt', art: 'zahl', wert: (z) => z.erfuellt },
    { schluessel: 'fehlt', titel: 'fehlt', art: 'zahl', wert: (z) => z.fehlt },
    { schluessel: 'ng', titel: 'nicht geplant', art: 'zahl', wert: (z) => z.nichtGeplant },
    { schluessel: 'oa', titel: 'ohne Anforderung', art: 'zahl', wert: (z) => z.ohneAnforderung },
    { schluessel: 'wid', titel: 'Quellen widersprechen', art: 'zahl', wert: (z) => z.quellenWidersprechen },
  ];
  return (
    <ContentSection titel="Statusmatrix je Gewerk"
                    beschreibung="Gefordert = Sicherheitskonzept oder Türbuch. Erfüllt = ein Bauteil der Art steht an der Tür (Türüberwachung: ein ZK-Bauteil, R15); fehlt = Gewerk im Modell, an dieser Tür nicht; nicht geplant = das Modell kennt keine Bauteilart dafür.">
      <DataTable name={`Gewerke (${zeilen.length})`} spalten={spalten} zeilen={zeilen}
                 schluessel={(z) => z.gewerk} suche={false} dicht />
    </ContentSection>
  );
}

function Tueren({ zeilen }: { zeilen: ZutrittTuer[] }) {
  const [nurWarnung, setNurWarnung] = useState(true);
  const [gewaehlt, setGewaehlt] = useState<ZutrittTuer | null>(null);
  const mitWarnung = zeilen.filter(hatWarnung);
  const gezeigt = nurWarnung ? mitWarnung : zeilen;
  const spalten: Spalte<ZutrittTuer>[] = [
    { schluessel: 'tuer', titel: 'Tür', wert: (z) => z.tuer },
    { schluessel: 'geschoss', titel: 'Geschoss', wert: (z) => z.geschoss },
    { schluessel: 'raum', titel: 'Raum', wert: (z) => z.raum },
    { schluessel: 'zutritt', titel: 'Zutritt', art: 'status', wert: (z) => z.zutritt,
      inhalt: (z) => <Statusmarke status={z.zutritt} /> },
    { schluessel: 'bauart', titel: 'Bauart', wert: (z) => z.zutrittBauart },
    { schluessel: 'flucht', titel: 'Fluchttür', art: 'status', wert: (z) => z.fluchttuer,
      inhalt: (z) => <Statusmarke status={z.fluchttuer} /> },
    { schluessel: 'ueberwachung', titel: 'Türüberwachung', art: 'status', wert: (z) => z.tuerueberwachung,
      inhalt: (z) => <Statusmarke status={z.tuerueberwachung} /> },
    { schluessel: 'sprech', titel: 'Sprechstelle', art: 'status', wert: (z) => z.sprechstelle,
      inhalt: (z) => <Statusmarke status={z.sprechstelle} /> },
    { schluessel: 'leser', titel: 'Leser', art: 'zahl', wert: (z) => z.leser },
    { schluessel: 'term', titel: 'Terminals', art: 'zahl', wert: (z) => z.terminals },
    { schluessel: 'strom', titel: 'Strom gefordert', wert: (z) => z.stromversorgung },
    { schluessel: 'versorgung', titel: 'Versorgung', wert: (z) => z.versorgung.split(', ').map(versorgungsname).join(', ') },
  ];
  return (
    <ContentSection titel="Türen"
                    beschreibung="Eine Tür je Türnummer. Eine Zeile anklicken zeigt die Quellen der Anforderung, die Bauteile und die Befunde.">
      <label className="klein">
        <input type="checkbox" checked={nurWarnung} onChange={(e) => setNurWarnung(e.target.checked)} />
        {' '}nur Türen mit Warnung ({mitWarnung.length} von {zeilen.length})
      </label>
      <DataTable name={`Türen (${gezeigt.length})`} spalten={spalten} zeilen={gezeigt}
                 schluessel={(z) => `${z.geschoss ?? ''}|${z.tuer}`} beiZeileKlick={(z) => setGewaehlt(z)} dicht />
      {gewaehlt && (
        <div className="karte">
          <h3>Tür {gewaehlt.tuer}</h3>
          <p className="klein">
            Zutritt gefordert durch: {gewaehlt.zutrittQuelle || '—'} · Fluchttür: {gewaehlt.fluchttuerQuelle || '—'}
            {' '}· Türüberwachung: {gewaehlt.tuerueberwachungQuelle || '—'} · Sprechstelle: {gewaehlt.sprechstelleQuelle || '—'}
          </p>
          <p className="klein">Bauteile: {gewaehlt.bauteile ?? '—'} · Türklasse: {gewaehlt.tuerklasse ?? '—'}</p>
          <Befundliste befunde={gewaehlt.befunde} />
        </div>
      )}
    </ContentSection>
  );
}

function OhneTuer({ zeilen }: { zeilen: ZutrittBauteil[] }) {
  if (zeilen.length === 0) return null;
  const spalten: Spalte<ZutrittBauteil>[] = [
    { schluessel: 'kennung', titel: 'Bauteil', wert: (z) => z.kennung },
    { schluessel: 'art', titel: 'Art', wert: (z) => z.bauteilart },
    { schluessel: 'geschoss', titel: 'Geschoss', wert: (z) => z.geschoss },
    { schluessel: 'raum', titel: 'Raum', wert: (z) => z.raum },
    { schluessel: 'grund', titel: 'Grund', wert: (z) => z.zuordnung },
    { schluessel: 'abstand', titel: 'nächste Tür', einheit: 'm', art: 'zahl', nachkomma: 2, wert: (z) => z.abstandM },
    { schluessel: 'zweite', titel: 'zweite', einheit: 'm', art: 'zahl', nachkomma: 2, wert: (z) => z.zweiteM },
    { schluessel: 'gespeichert', titel: 'gespeichert', wert: (z) => z.tuerGespeichert },
  ];
  return (
    <ContentSection titel="Leser und Terminals ohne Tür"
                    beschreibung="Nächste Tür im Höhenfenster und im Umkreis; eine zweite Tür anderer Nummer muss mehr als den Vorsprung weiter liegen; der Raum des Bauteils muss ein Raum der Tür sein.">
      <DataTable name={`Ohne Tür (${zeilen.length})`} spalten={spalten} zeilen={zeilen}
                 schluessel={(z) => z.kennung} dicht />
    </ContentSection>
  );
}

function Versorgung({ zeilen }: { zeilen: ZutrittVersorgung[] }) {
  const spalten: Spalte<ZutrittVersorgung>[] = [
    { schluessel: 'verteiler', titel: 'Verteiler', wert: (z) => z.verteiler },
    { schluessel: 'art', titel: 'Art', wert: (z) => z.art },
    { schluessel: 'vart', titel: 'Versorgung', wert: (z) => z.versorgungsart },
    { schluessel: 'leser', titel: 'Leser', art: 'zahl', wert: (z) => z.leser },
    { schluessel: 'term', titel: 'Terminals', art: 'zahl', wert: (z) => z.terminals },
    { schluessel: 'mit', titel: 'mit Stromkreis', art: 'zahl', wert: (z) => z.mitStromkreis },
    { schluessel: 'poe', titel: 'PoE', art: 'zahl', wert: (z) => z.poe },
    { schluessel: 'ohne', titel: 'ohne Stromkreis', art: 'zahl', wert: (z) => z.ohneStromkreis },
    { schluessel: 'p', titel: 'P Terminals', einheit: 'W', art: 'zahl', nachkomma: 0, wert: (z) => z.terminalleistungW },
  ];
  return (
    <ContentSection titel="Versorgung je Verteiler"
                    beschreibung="Leser beziehen PoE aus dem IT-Verteiler (Bilanz in der IT-Bestückung). Fluchttürterminals brauchen einen Stromkreis bzw. ein Netzteil; die Leistung je Terminal ist eine Vorgabe.">
      <DataTable name={`Verteiler (${zeilen.length})`} spalten={spalten} zeilen={zeilen}
                 schluessel={(z) => z.verteiler} dicht />
    </ContentSection>
  );
}

function Vorgaben({ zeilen }: { zeilen: StVorgabe[] }) {
  const spalten: Spalte<StVorgabe>[] = [
    { schluessel: 'feld', titel: 'Vorgabe', wert: (z) => z.feld },
    { schluessel: 'wert', titel: 'Wert', wert: (z) => `${z.wert}${z.einheit ? ` ${z.einheit}` : ''}` },
    { schluessel: 'bestaetigt', titel: 'bestätigt', wert: (z) => (z.bestaetigt ? 'ja' : 'nein') },
    { schluessel: 'herkunft', titel: 'Herkunft', wert: (z) => `${z.herkunft}${z.rueckfall ? ' (Rückfall)' : ''}` },
    { schluessel: 'norm', titel: 'Normbezug', wert: (z) => z.normbezug },
  ];
  return (
    <ContentSection titel="Vorgaben (Migration 1040)">
      <DataTable name={`Vorgaben (${zeilen.length})`} spalten={spalten} zeilen={zeilen}
                 schluessel={(z) => `${z.feld}|${z.verwendung ?? ''}`} suche={false} dicht />
    </ContentSection>
  );
}
